/**
 * advance-structures.ts — per-tick planet-side structure pass.
 *
 * Runs inside advanceTick after advancePopulationAndEconomy (so upkeep
 * deducts against income that landed this tick) and before the ship
 * advance passes. Per active structure it does three things:
 *
 *   1. Deduct upkeep (STRUCTURE_UPKEEP_CREDITS_PER_TICK credits) from the
 *      owning empire. Only active structures (level >= 1) pay — a structure
 *      under initial construction is not yet operational. Empires are
 *      allowed to go negative on credits (matches how jumps consume fuel
 *      without requiring positive balance upfront); a sustained negative
 *      balance becomes a player concern, not a sim-layer error.
 *
 *   2. Auto-repair currentHull toward maxHull at
 *      STRUCTURE_AUTO_REPAIR_PROPORTION_PER_TICK × maxHull per tick. No-op
 *      in v1 play (no damage source yet) but the surface is live so
 *      bombardment lands into a working repair loop later.
 *
 *   3. Tick the build/upgrade countdown. On reaching 0, promote level++
 *      and refit hull to the new level's ceiling. Structures that promote
 *      this tick start paying upkeep next tick (upkeep ran before the
 *      promotion check intentionally — you're billed for what you had at
 *      the start of the tick, not what you're getting at the end).
 *
 * Deterministic iteration: structures visited in ascending entity id so
 * the tick is reproducible across hosts (MULTIPLAYER §2).
 */

import {
  autoRepairProportion,
  maxHullForLevel,
  upkeepForKind,
} from './balance-structures';
import type { GameState, Structure, StructuresData } from '../state/types';

/**
 * Advance planet-side structures by exactly one tick. Mutates state.
 *
 * Construction is **sequential per planet (FIFO by structure id)**: if a
 * planet has multiple in-flight structures (either pending first-builds
 * or queued upgrades), only the lowest-id one ticks down each tick;
 * later-queued structures wait their turn. Upkeep and auto-repair still
 * apply in parallel to every active structure — sequentiality only gates
 * the construction pipeline, not maintenance.
 *
 * Why sequential: matches the shipyard build queue pattern (one active
 * build, others wait) and avoids the earlier behaviour where queuing N
 * structures silently started N parallel builds. A planet has one
 * construction capacity; the order is the order the player clicked.
 */
export function advanceStructures(state: GameState): void {
  const planets = [...state.planets.values()].sort((a, b) => a.id - b.id);
  const sd = state.structuresData;
  const repairFraction = autoRepairProportion(sd);

  for (const planet of planets) {
    if (planet.ownerEmpireId === null) continue;
    const empire = state.empires.get(planet.ownerEmpireId);
    if (!empire) continue;

    // Gather this planet's structures.
    const planetStructures: Structure[] = [];
    for (const sid of planet.structureIds) {
      const s = state.structures.get(sid);
      if (s) planetStructures.push(s);
    }

    // 1. Upkeep + auto-repair for every ACTIVE structure on the planet.
    //    These two run in parallel — every commissioned structure bills
    //    its upkeep each tick and self-heals its hull independently.
    //    Order doesn't matter here, but sort by id so the pass stays
    //    deterministic (e.g. empire.resources.credits is mutated once
    //    per active structure; order is observable via save diffing).
    planetStructures.sort((a, b) => a.id - b.id);
    for (const structure of planetStructures) {
      if (structure.level < 1) continue;
      empire.resources.credits -= upkeepForKind(sd, structure.kind);
      if (structure.currentHull < structure.maxHull) {
        const repair = structure.maxHull * repairFraction;
        structure.currentHull = Math.min(
          structure.maxHull,
          structure.currentHull + repair,
        );
      }
    }

    // 2. Construction / upgrade — tick the structure the player queued
    //    FIRST among all in-flight ones on this planet (lowest queuedAt
    //    timestamp). Id is the deterministic tie-breaker when two
    //    commands land on the same tick. This preserves player click
    //    order rather than underlying build order, so upgrading an
    //    older structure after a newer one doesn't let the older jump
    //    the queue.
    let active: Structure | null = null;
    for (const structure of planetStructures) {
      if (structure.buildTicksRemaining <= 0) continue;
      if (
        active === null ||
        structure.queuedAt < active.queuedAt ||
        (structure.queuedAt === active.queuedAt && structure.id < active.id)
      ) {
        active = structure;
      }
    }
    if (active) {
      active.buildTicksRemaining -= 1;
      if (active.buildTicksRemaining === 0) {
        promoteLevel(sd, active);
      }
    }
  }
}

/**
 * Advance a structure's active level by one and refit hull to the new
 * ceiling. Called the tick a buildTicksRemaining countdown hits 0 —
 * either finishing an initial construction (level 0 → 1) or finishing
 * an upgrade (N → N+1).
 *
 * Refits currentHull = maxHull on promotion (upgrade "includes a refit"
 * semantics) so a damaged structure comes out of an upgrade fully healed.
 * Level is clamped to 6 defensively; the command layer refuses to queue
 * an upgrade past 6 but keep the clamp here so a bad state can't explode.
 */
function promoteLevel(structures: StructuresData, structure: Structure): void {
  const nextLevel = Math.min(6, structure.level + 1);
  const lookup = maxHullForLevel(structures, structure.kind, nextLevel);
  const newMaxHull = lookup > 0 ? lookup : structure.maxHull;
  structure.level = nextLevel;
  structure.maxHull = newMaxHull;
  structure.currentHull = newMaxHull;
}
